// Arabic tools — Tools index + PDPL Readiness + ISO 27001 Readiness +
// Software Cost Estimator + AI Opportunity Finder. All scoring logic and
// backend endpoints are identical to the English versions; this file is
// a language layer, not a logic change. Same POSTs to /api/tools/*.
import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ShieldCheck, Award, Calculator, Sparkles, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Seo } from "@/components/Seo";

// -------- Shared shell / option / nav (RTL-aware) --------
function ArToolShell({ eyebrow, title, subtitle, step, totalSteps, children }) {
  const progress = totalSteps ? Math.round((step / totalSteps) * 100) : 0;
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-3xl px-4 py-16 lg:px-6 lg:py-24">
        <div className="text-xs font-semibold uppercase tracking-widest text-wafa-primary">{eyebrow}</div>
        <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">{title}</h1>
        {subtitle ? <p className="mt-4 text-sm text-wafa-ink/70 lg:text-base">{subtitle}</p> : null}
        {totalSteps ? (
          <div className="mt-8">
            <div className="flex items-center justify-between text-xs text-wafa-ink/60">
              <span>الخطوة {Math.min(step, totalSteps)} من {totalSteps}</span>
              <span>{progress}%</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-wafa-deep/10">
              <div className="h-full bg-wafa-primary transition-[width] duration-300 ease-out" style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : null}
        <div className="mt-10">{children}</div>
      </div>
    </section>
  );
}

function ArOptionRow({ label, value, current, onChange, testId }) {
  const active = current === value;
  return (
    <button type="button" onClick={() => onChange(value)} data-testid={testId}
      className={cn("w-full rounded-md border px-4 py-3 text-start text-sm transition-colors",
        active ? "border-wafa-primary bg-wafa-primary/5 text-wafa-deep font-semibold"
               : "border-wafa-deep/15 bg-white text-wafa-ink hover:border-wafa-deep/40")}>
      {label}
    </button>
  );
}

function ArToolNav({ onBack, onNext, disableNext, nextLabel = "التالي" }) {
  return (
    <div className="mt-10 flex items-center justify-between gap-3">
      <button type="button" onClick={onBack} disabled={!onBack}
        className="rounded-md border border-wafa-deep/15 px-4 py-2 text-sm font-medium text-wafa-deep disabled:opacity-40" data-testid="tool-back-ar">
        السابق
      </button>
      <button type="button" onClick={onNext} disabled={disableNext}
        className="rounded-md bg-wafa-primary px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-wafa-deep disabled:opacity-50" data-testid="tool-next-ar">
        {nextLabel}
      </button>
    </div>
  );
}

const OPTIONS_YNP_AR = [
  { value: "yes", label: "نعم", weight: 1 },
  { value: "partial", label: "جزئيًا", weight: 0.5 },
  { value: "no", label: "لا", weight: 0 },
];

// -------- Tools index --------
const TOOLS_AR = [
  { to: "/ar/tools/pdpl-readiness-check", icon: ShieldCheck, title: "فحص جاهزية قانون حماية البيانات", body: "12 سؤالًا: نعم / جزئيًا / لا. درجة من 100، وأهم ثلاث فجوات.", time: "3 دقائق" },
  { to: "/ar/tools/iso-27001-readiness", icon: Award, title: "درجة جاهزية ISO 27001", body: "10 أسئلة عن ملكية الضوابط والاختبار والسياسات والأدلة.", time: "4 دقائق" },
  { to: "/ar/tools/software-cost-estimator", icon: Calculator, title: "حاسبة تكلفة البرمجيات", body: "نطاق تكلفة تقريبي بالريال العُماني وجدول زمني تقديري.", time: "دقيقتان" },
  { to: "/ar/tools/ai-opportunity-finder", icon: Sparkles, title: "مكتشف فرص الذكاء الاصطناعي", body: "اختاروا صناعتكم ونقاط الألم — نعيد ثلاث حالات استخدام بتقييم الجهد والقيمة.", time: "3 دقائق" },
];

export function ToolsIndexAr() {
  return (
    <div data-testid="tools-index-ar">
      <Seo title="أدوات مجانية" description="أربع أدوات سريعة تعطيكم إجابة حقيقية خلال دقائق." />
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 lg:px-6 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-wafa-carrot/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-wafa-carrot">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-wafa-carrot" />
              أدوات مجانية جديدة
            </div>
            <h1 className="mt-4 font-heading text-4xl font-semibold tracking-tight text-wafa-deep sm:text-5xl lg:text-6xl">
              أربعة فحوصات قصيرة. إجابات حقيقية، فورًا.
            </h1>
            <p className="mt-5 text-base text-wafa-ink/80 lg:text-lg">
              بنينا هذه لتروا موقعكم قبل التحدث مع أي أحد — بمن فينا. كل نتيجة تظهر على الشاشة. لا مكالمة مبيعات مطلوبة.
            </p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {TOOLS_AR.map(({ to, icon: Icon, title, body, time }) => (
              <Link key={to} to={to} data-testid={`tool-link-ar-${to}`} className="group flex flex-col rounded-lg border border-wafa-deep/10 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:border-wafa-deep/25 hover:shadow-[0_20px_50px_-20px_rgba(14,79,58,0.25)]">
                <div className="flex items-center justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-md bg-wafa-cream/70 text-wafa-deep group-hover:bg-wafa-primary group-hover:text-white transition-colors">
                    <Icon className="h-6 w-6" />
                  </div>
                  <span className="rounded-full bg-wafa-cream px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-wafa-deep">{time}</span>
                </div>
                <div className="mt-6 font-heading text-2xl font-semibold text-wafa-deep">{title}</div>
                <p className="mt-3 text-sm text-wafa-ink/75">{body}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-wafa-deep">
                  ابدأوا الفحص <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// -------- PDPL Readiness --------
const PDPL_QUESTIONS_AR = [
  { key: "consent_explicit", section: "الموافقة", text: "هل تسجّلون موافقة موثقة قبل معالجة البيانات الشخصية؟" },
  { key: "consent_withdraw", section: "الموافقة", text: "هل يمكن للأفراد سحب موافقتهم بنفس سهولة إعطائها؟" },
  { key: "notice_public", section: "إشعارات الخصوصية", text: "هل لديكم إشعار خصوصية باللغتين العربية والإنجليزية؟" },
  { key: "notice_updated", section: "إشعارات الخصوصية", text: "هل تمّت مراجعة إشعار الخصوصية وتحديثه خلال 12 شهرًا؟" },
  { key: "dsar_process", section: "طلبات الأفراد", text: "هل يمكنكم الرد على طلب فرد للوصول لبياناته خلال 45 يومًا؟" },
  { key: "dsar_log", section: "طلبات الأفراد", text: "هل تُسجّلون طلبات الأفراد ونتائجها لأغراض التدقيق؟" },
  { key: "dpo_named", section: "مسؤول حماية البيانات", text: "هل عيّنتم مسؤولًا لحماية البيانات ببيانات تواصل معلنة؟" },
  { key: "dpo_reporting", section: "مسؤول حماية البيانات", text: "هل يرفع مسؤول الحماية تقاريره للإدارة العليا باستقلالية؟" },
  { key: "xborder_map", section: "النقل عبر الحدود", text: "هل تُقيّمون مستوى الحماية قبل نقل البيانات خارج عُمان؟" },
  { key: "xborder_controls", section: "النقل عبر الحدود", text: "هل حصلتم على موافقة الوزارة لأي بيانات حساسة تُعالَج خارج عُمان؟" },
  { key: "breach_process", section: "عملية الخروقات", text: "هل لديكم عملية إبلاغ عن الخروقات تعمل خلال 72 ساعة؟" },
  { key: "breach_drill", section: "عملية الخروقات", text: "هل اختبرتم هذه العملية خلال 12 شهرًا بتدريب أو حادث حقيقي؟" },
];

function computeScorePdpl(answers) {
  const perSection = {}; let total = 0;
  PDPL_QUESTIONS_AR.forEach((q) => {
    const opt = OPTIONS_YNP_AR.find((o) => o.value === answers[q.key]);
    const w = opt ? opt.weight : 0; total += w;
    perSection[q.section] = perSection[q.section] || { got: 0, max: 0 };
    perSection[q.section].got += w; perSection[q.section].max += 1;
  });
  const score = Math.round((total / PDPL_QUESTIONS_AR.length) * 100);
  const sectionScores = {};
  Object.entries(perSection).forEach(([s, v]) => {
    const pct = v.got / v.max;
    sectionScores[s] = pct >= 0.75 ? "green" : pct >= 0.4 ? "amber" : "red";
  });
  const gaps = PDPL_QUESTIONS_AR.filter((q) => answers[q.key] !== "yes").slice(0, 3).map((q) => q.text);
  return { score, sectionScores, gaps };
}

export function PdplReadinessAr() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", company: "" });
  const [state, setState] = useState({ stage: "quiz", error: "" });
  const total = PDPL_QUESTIONS_AR.length + 1;

  if (state.stage === "result") {
    const { score, sectionScores, gaps } = state.result;
    const bandLabel = score >= 75 ? "جاهزون إلى حد كبير" : score >= 40 ? "تقدّم جيد، فجوات متبقية" : "بحاجة للعمل الآن";
    return (
      <ArToolShell eyebrow="فحص جاهزية قانون حماية البيانات" title="نتيجتكم">
        <Seo title="نتيجة فحص جاهزية قانون حماية البيانات" />
        <div className="rounded-2xl border border-wafa-deep/10 bg-wafa-cream/40 p-8 text-center">
          <div className="font-heading text-6xl font-semibold text-wafa-deep">{score}</div>
          <div className="mt-1 text-xs uppercase tracking-widest text-wafa-ink/60">من أصل 100</div>
          <div className="mt-4 inline-flex rounded-full bg-wafa-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-wafa-deep">{bandLabel}</div>
        </div>
        <div className="mt-8">
          <div className="font-heading text-lg font-semibold text-wafa-deep">حسب القسم</div>
          <div className="mt-4 grid gap-3">
            {Object.entries(sectionScores).map(([s, level]) => {
              const dot = level === "green" ? "bg-wafa-primary" : level === "amber" ? "bg-wafa-sunshine" : "bg-wafa-tomato";
              const lbl = level === "green" ? "أخضر" : level === "amber" ? "أصفر" : "أحمر";
              return (
                <div key={s} data-testid={`pdpl-section-ar-${s}`} className="flex items-center justify-between rounded-md border border-wafa-deep/10 bg-white px-4 py-3 text-sm">
                  <span className="font-medium text-wafa-deep">{s}</span>
                  <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-wafa-ink/70">
                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${dot}`} />{lbl}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        {gaps.length ? (
          <div className="mt-8">
            <div className="font-heading text-lg font-semibold text-wafa-deep">أهم ثلاث فجوات لديكم</div>
            <ul className="mt-3 space-y-2">
              {gaps.map((g, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-wafa-ink/80">
                  <AlertCircle className="mt-0.5 h-4 w-4 text-wafa-carrot" /><span>{g}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className="mt-10 rounded-lg border border-wafa-deep/10 bg-white p-6">
          <div className="font-heading font-semibold text-wafa-deep">تريدون أن نأخذكم خلالها؟</div>
          <p className="mt-1 text-sm text-wafa-ink/70">احجزوا مكالمة ثلاثين دقيقة وسنحوّل هذه إلى خطة.</p>
          <Link to="/ar/company/contact" className="mt-4 inline-flex rounded-md bg-wafa-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-wafa-deep" data-testid="pdpl-result-cta-ar">تحدثوا معنا عن الامتثال</Link>
        </div>
      </ArToolShell>
    );
  }

  if (state.stage === "gate") {
    const submit = async (e) => {
      e.preventDefault();
      if (!contact.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) { setState({ ...state, error: "يرجى إدخال بريد إلكتروني صحيح." }); return; }
      const result = computeScorePdpl(answers);
      try {
        await api.post("/tools/pdpl-readiness", {
          answers: Object.entries(answers).map(([key, value]) => ({ key, value })),
          score: result.score, section_scores: result.sectionScores, gaps: result.gaps,
          email: contact.email, name: contact.name, company: contact.company,
        });
        setState({ stage: "result", result });
      } catch (_) { setState({ ...state, error: "حدث خطأ. يرجى المحاولة مجددًا." }); }
    };
    return (
      <ArToolShell eyebrow="فحص جاهزية قانون حماية البيانات" title="اطلعوا على نتيجتكم" subtitle="سنُرسل لكم نسخة إلى بريدكم لسجلاتكم." step={total} totalSteps={total}>
        <Seo title="فحص جاهزية قانون حماية البيانات" />
        <form onSubmit={submit} className="space-y-4" data-testid="pdpl-gate-form-ar">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-wafa-deep">الاسم</label>
            <input data-testid="pdpl-name-ar" className="mt-2 h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-wafa-deep">البريد الإلكتروني للعمل *</label>
            <input type="email" required data-testid="pdpl-email-ar" className="mt-2 h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-wafa-deep">الشركة</label>
            <input data-testid="pdpl-company-ar" className="mt-2 h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} />
          </div>
          {state.error ? <div className="flex items-center gap-2 text-sm text-wafa-tomato" data-testid="pdpl-error-ar"><AlertCircle className="h-4 w-4" />{state.error}</div> : null}
          <button type="submit" className="w-full rounded-md bg-wafa-primary py-3 text-sm font-semibold text-white hover:bg-wafa-deep" data-testid="pdpl-see-score-ar">اطلعوا على نتيجتي</button>
        </form>
      </ArToolShell>
    );
  }

  const q = PDPL_QUESTIONS_AR[step]; const answered = !!answers[q.key];
  return (
    <ArToolShell eyebrow="فحص جاهزية قانون حماية البيانات" title="12 سؤالًا. 3 دقائق." subtitle="نعم / جزئيًا / لا — أجيبوا كما تعمل الأمور فعلًا اليوم، لا كما تخططون لها." step={step + 1} totalSteps={total}>
      <Seo title="فحص جاهزية قانون حماية البيانات" />
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-wafa-primary">{q.section}</div>
        <div className="mt-2 font-heading text-xl font-medium text-wafa-deep sm:text-2xl">{q.text}</div>
        <div className="mt-6 space-y-2.5">
          {OPTIONS_YNP_AR.map((o) => (
            <ArOptionRow key={o.value} value={o.value} current={answers[q.key]} onChange={(v) => setAnswers({ ...answers, [q.key]: v })} label={o.label} testId={`pdpl-option-ar-${q.key}-${o.value}`} />
          ))}
        </div>
      </div>
      <ArToolNav onBack={step > 0 ? () => setStep(step - 1) : null}
        onNext={() => { if (step === PDPL_QUESTIONS_AR.length - 1) setState({ stage: "gate", error: "" }); else setStep(step + 1); }}
        disableNext={!answered} nextLabel={step === PDPL_QUESTIONS_AR.length - 1 ? "إنهاء" : "التالي"} />
    </ArToolShell>
  );
}

// -------- ISO 27001 --------
const ISO_QUESTIONS_AR = [
  { key: "control_owners", text: "هل لدى كل ضابط أمني مالك مسمّى؟" },
  { key: "controls_tested", text: "هل تُختبر الضوابط على جدول منتظم؟" },
  { key: "policies_current", text: "هل تُحفَظ أدلة الاختبار وليس فقط نتيجته؟" },
  { key: "evidence_ready", text: "هل لديكم سياسات أمنية مكتوبة ومعتمدة؟" },
  { key: "risk_register", text: "هل وقّع الموظفون على إقرار بقراءة السياسات؟" },
  { key: "supplier_reviews", text: "هل لديكم بيان انطباق (Statement of Applicability) محدَّث؟" },
  { key: "training_program", text: "هل تُقيَّم مخاطر الموردين قبل التعاقد وبشكل دوري؟" },
  { key: "incident_playbook", text: "هل لديكم سجل حوادث أمنية موثّق؟" },
  { key: "access_reviews", text: "هل أُجري تدقيق داخلي واحد على الأقل خلال العام الماضي؟" },
  { key: "management_review", text: "هل يمكنكم إنتاج أدلة الامتثال خلال أسبوع إذا طُلبت؟" },
];

function computeIso(answers) {
  let total = 0;
  ISO_QUESTIONS_AR.forEach((q) => { const o = OPTIONS_YNP_AR.find((x) => x.value === answers[q.key]); total += o ? o.weight : 0; });
  const score = Math.round((total / ISO_QUESTIONS_AR.length) * 100);
  let band = "بداية الرحلة";
  if (score >= 80) band = "جاهزون للتدقيق"; else if (score >= 50) band = "على الطريق الصحيح";
  const topFix = ISO_QUESTIONS_AR.find((q) => answers[q.key] !== "yes")?.text || "حافظوا على تحديث اختبارات الضوابط.";
  return { score, band, topFix };
}

export function IsoReadinessAr() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", company: "" });
  const [state, setState] = useState({ stage: "quiz", error: "" });
  const total = ISO_QUESTIONS_AR.length + 1;

  if (state.stage === "result") {
    const { score, band, topFix } = state.result;
    return (
      <ArToolShell eyebrow="درجة جاهزية ISO 27001" title="نتيجة جاهزيتكم">
        <Seo title="نتيجة جاهزية ISO 27001" />
        <div className="rounded-2xl border border-wafa-deep/10 bg-wafa-cream/40 p-8 text-center">
          <div className="font-heading text-6xl font-semibold text-wafa-deep">{score}</div>
          <div className="mt-1 text-xs uppercase tracking-widest text-wafa-ink/60">من أصل 100</div>
          <div className="mt-4 inline-flex rounded-full bg-wafa-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-wafa-deep">{band}</div>
        </div>
        <div className="mt-8 rounded-lg border border-wafa-deep/10 bg-white p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-wafa-primary">ماذا تُصلحون أولًا</div>
          <div className="mt-2 text-base text-wafa-ink/85">{topFix}</div>
        </div>
        <Link to="/ar/meyar/cyber-security-grc" data-testid="iso-result-cta-ar" className="mt-10 inline-flex w-full items-center justify-center rounded-md bg-wafa-primary px-5 py-3 text-sm font-semibold text-white hover:bg-wafa-deep">تعرّفوا على Meyar Cyber Security GRC</Link>
      </ArToolShell>
    );
  }

  if (state.stage === "gate") {
    const submit = async (e) => {
      e.preventDefault();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) { setState({ ...state, error: "يرجى إدخال بريد إلكتروني صحيح." }); return; }
      const r = computeIso(answers);
      try {
        await api.post("/tools/iso-27001-readiness", {
          answers: Object.entries(answers).map(([k, v]) => ({ key: k, value: v })),
          score: r.score, band: r.band, top_fix: r.topFix,
          email: contact.email, name: contact.name, company: contact.company,
        });
        setState({ stage: "result", result: r });
      } catch (_) { setState({ ...state, error: "فشل الإرسال. يرجى المحاولة مجددًا." }); }
    };
    return (
      <ArToolShell eyebrow="درجة جاهزية ISO 27001" title="اطلعوا على نتيجتكم" step={total} totalSteps={total}>
        <Seo title="درجة جاهزية ISO 27001" />
        <form onSubmit={submit} className="space-y-4" data-testid="iso-gate-form-ar">
          <input placeholder="الاسم" data-testid="iso-name-ar" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
          <input required type="email" placeholder="البريد الإلكتروني للعمل *" data-testid="iso-email-ar" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
          <input placeholder="الشركة" data-testid="iso-company-ar" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} />
          {state.error ? <div className="flex items-center gap-2 text-sm text-wafa-tomato"><AlertCircle className="h-4 w-4" />{state.error}</div> : null}
          <button data-testid="iso-see-score-ar" className="w-full rounded-md bg-wafa-primary py-3 text-sm font-semibold text-white hover:bg-wafa-deep">اطلعوا على نتيجتي</button>
        </form>
      </ArToolShell>
    );
  }

  const q = ISO_QUESTIONS_AR[step]; const answered = !!answers[q.key];
  return (
    <ArToolShell eyebrow="درجة جاهزية ISO 27001" title="10 أسئلة." subtitle="أجيبوا كما تعمل الأمور فعلًا اليوم." step={step + 1} totalSteps={total}>
      <Seo title="درجة جاهزية ISO 27001" />
      <div className="font-heading text-xl font-medium text-wafa-deep sm:text-2xl">{q.text}</div>
      <div className="mt-6 space-y-2.5">
        {OPTIONS_YNP_AR.map((o) => (
          <ArOptionRow key={o.value} value={o.value} current={answers[q.key]} onChange={(v) => setAnswers({ ...answers, [q.key]: v })} label={o.label} testId={`iso-option-ar-${q.key}-${o.value}`} />
        ))}
      </div>
      <ArToolNav onBack={step > 0 ? () => setStep(step - 1) : null}
        onNext={() => { if (step === ISO_QUESTIONS_AR.length - 1) setState({ stage: "gate", error: "" }); else setStep(step + 1); }}
        disableNext={!answered} nextLabel={step === ISO_QUESTIONS_AR.length - 1 ? "إنهاء" : "التالي"} />
    </ArToolShell>
  );
}

// -------- Software Cost Estimator --------
const PROJECT_TYPES_AR = [
  { key: "custom-web", label: "أداة داخلية", base: 12000 },
  { key: "mobile-app", label: "منصة أعمال", base: 15000 },
  { key: "saas", label: "منصة بتطبيق جوال", base: 25000 },
  { key: "erp", label: "منتج SaaS", base: 30000 },
];
const TIMELINE_OPTS_AR = ["مستعجل", "1-3 أشهر", "3-6 أشهر", "نستكشف الخيارات فقط"];

function bandAr(cost) {
  const fmt = (n) => Math.round(n).toLocaleString("en-US");
  if (cost < 8000) return "أقل من 8,000 ريال عُماني";
  if (cost < 20000) return `${fmt(cost - 3000)} – ${fmt(cost + 4000)} ريال عُماني`;
  if (cost < 50000) return `${fmt(cost - 6000)} – ${fmt(cost + 10000)} ريال عُماني`;
  return `${fmt(cost - 10000)} – ${fmt(cost + 20000)} ريال عُماني`;
}
function timelineRangeAr(m) { const min = Math.max(1, m - 1); const max = m + 2; return `${min}–${max} أشهر`; }

export function SoftwareCostAr() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ project_type: "", roles: 3, mobile: null, integrations: 2, timeline: "" });
  const [contact, setContact] = useState({ name: "", email: "", company: "" });
  const [state, setState] = useState({ stage: "quiz", error: "" });
  const total = 6;

  const calc = () => {
    const pt = PROJECT_TYPES_AR.find((p) => p.key === form.project_type) || PROJECT_TYPES_AR[0];
    let cost = pt.base + form.roles * 800 + form.integrations * 1500;
    if (form.mobile) cost += 6000;
    if (form.timeline === "مستعجل") cost *= 1.2;
    const months = form.timeline === "مستعجل" ? 2 : form.timeline.includes("1-3") ? 3 : form.timeline.includes("3-6") ? 5 : 4;
    return { cost_band_omr: bandAr(cost), timeline_range: timelineRangeAr(months) };
  };

  if (state.stage === "result") {
    const { cost_band_omr, timeline_range } = state.result;
    return (
      <ArToolShell eyebrow="حاسبة تكلفة البرمجيات" title="النطاق التقديري">
        <Seo title="نطاق تكلفة البرمجيات التقديري" />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-wafa-deep/10 bg-wafa-cream/40 p-6">
            <div className="text-xs uppercase tracking-wider text-wafa-ink/60">نطاق التكلفة (ريال عُماني)</div>
            <div className="mt-3 font-heading text-2xl font-semibold text-wafa-deep">{cost_band_omr}</div>
          </div>
          <div className="rounded-lg border border-wafa-deep/10 bg-wafa-cream/40 p-6">
            <div className="text-xs uppercase tracking-wider text-wafa-ink/60">الجدول الزمني التقديري</div>
            <div className="mt-3 font-heading text-2xl font-semibold text-wafa-deep">{timeline_range}</div>
          </div>
        </div>
        <p className="mt-6 text-xs text-wafa-ink/60">
          <span className="font-semibold text-wafa-deep">هذا رقم إرشادي فقط.</span> السعر الفعلي يُحدَّد بعد جلسة تحديد النطاق.
        </p>
        <Link to="/ar/company/contact" data-testid="cost-result-cta-ar" className="mt-8 inline-flex w-full items-center justify-center rounded-md bg-wafa-primary px-5 py-3 text-sm font-semibold text-white hover:bg-wafa-deep">احجزوا مكالمة تحديد النطاق المجانية</Link>
      </ArToolShell>
    );
  }

  if (state.stage === "gate") {
    const submit = async (e) => {
      e.preventDefault();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) { setState({ ...state, error: "يرجى إدخال بريد إلكتروني صحيح." }); return; }
      const r = calc();
      try {
        await api.post("/tools/software-cost", {
          project_type: form.project_type, roles_count: Number(form.roles) || 0, mobile_app: !!form.mobile,
          integrations: Number(form.integrations) || 0, timeline: form.timeline,
          cost_band_omr: r.cost_band_omr, timeline_range: r.timeline_range,
          email: contact.email, name: contact.name, company: contact.company,
        });
        setState({ stage: "result", result: r });
      } catch (_) { setState({ ...state, error: "فشل الإرسال. يرجى المحاولة مجددًا." }); }
    };
    return (
      <ArToolShell eyebrow="حاسبة تكلفة البرمجيات" title="اطلعوا على تقديركم" step={total} totalSteps={total}>
        <Seo title="حاسبة تكلفة البرمجيات" />
        <form onSubmit={submit} className="space-y-4" data-testid="cost-gate-form-ar">
          <input placeholder="الاسم" data-testid="cost-name-ar" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
          <input required type="email" placeholder="البريد الإلكتروني للعمل *" data-testid="cost-email-ar" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
          <input placeholder="الشركة" data-testid="cost-company-ar" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} />
          {state.error ? <div className="flex items-center gap-2 text-sm text-wafa-tomato"><AlertCircle className="h-4 w-4" />{state.error}</div> : null}
          <button data-testid="cost-see-result-ar" className="w-full rounded-md bg-wafa-primary py-3 text-sm font-semibold text-white hover:bg-wafa-deep">اطلعوا على تقديري</button>
        </form>
      </ArToolShell>
    );
  }

  const canNext = () => {
    if (step === 0) return !!form.project_type;
    if (step === 1) return form.roles !== "" && form.roles !== null;
    if (step === 2) return form.mobile !== null;
    if (step === 3) return form.integrations !== "" && form.integrations !== null;
    if (step === 4) return !!form.timeline;
    return true;
  };

  return (
    <ArToolShell eyebrow="حاسبة تكلفة البرمجيات" title="تقدير سريع وإرشادي." subtitle="خمسة أسئلة. الأرقام الفعلية تأتي بعد جلسة تحديد النطاق." step={step + 1} totalSteps={total}>
      <Seo title="حاسبة تكلفة البرمجيات" />
      {step === 0 && (
        <div>
          <div className="font-heading text-xl font-medium text-wafa-deep">نوع المشروع</div>
          <div className="mt-6 space-y-2.5">
            {PROJECT_TYPES_AR.map((p) => (
              <ArOptionRow key={p.key} value={p.key} current={form.project_type} onChange={(v) => setForm({ ...form, project_type: v })} label={p.label} testId={`cost-project-ar-${p.key}`} />
            ))}
          </div>
        </div>
      )}
      {step === 1 && (
        <div>
          <div className="font-heading text-xl font-medium text-wafa-deep">عدد أنواع المستخدمين</div>
          <div className="mt-6">
            <input type="range" min={1} max={12} step={1} value={form.roles} onChange={(e) => setForm({ ...form, roles: Number(e.target.value) })} data-testid="cost-roles-slider-ar" className="w-full accent-wafa-primary" />
            <div className="mt-2 text-sm text-wafa-ink/70">{form.roles} نوع</div>
          </div>
        </div>
      )}
      {step === 2 && (
        <div>
          <div className="font-heading text-xl font-medium text-wafa-deep">هل يشمل تطبيق جوال؟</div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <ArOptionRow value={true} current={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} label="نعم" testId="cost-mobile-yes-ar" />
            <ArOptionRow value={false} current={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} label="لا" testId="cost-mobile-no-ar" />
          </div>
        </div>
      )}
      {step === 3 && (
        <div>
          <div className="font-heading text-xl font-medium text-wafa-deep">عدد التكاملات مع أنظمة أخرى</div>
          <div className="mt-6">
            <input type="range" min={0} max={15} step={1} value={form.integrations} onChange={(e) => setForm({ ...form, integrations: Number(e.target.value) })} data-testid="cost-integrations-slider-ar" className="w-full accent-wafa-primary" />
            <div className="mt-2 text-sm text-wafa-ink/70">{form.integrations} تكامل</div>
          </div>
        </div>
      )}
      {step === 4 && (
        <div>
          <div className="font-heading text-xl font-medium text-wafa-deep">الجدول الزمني المطلوب</div>
          <div className="mt-6 space-y-2.5">
            {TIMELINE_OPTS_AR.map((t) => (
              <ArOptionRow key={t} value={t} current={form.timeline} onChange={(v) => setForm({ ...form, timeline: v })} label={t} testId={`cost-timeline-ar-${t}`} />
            ))}
          </div>
        </div>
      )}
      <ArToolNav onBack={step > 0 ? () => setStep(step - 1) : null}
        onNext={() => { if (step === total - 2) setState({ stage: "gate", error: "" }); else setStep(step + 1); }}
        disableNext={!canNext()} nextLabel={step === total - 2 ? "إنهاء" : "التالي"} />
    </ArToolShell>
  );
}

// -------- AI Opportunity Finder --------
const INDUSTRIES_AR = [
  "الخدمات اللوجستية والنقل", "التجزئة والتجارة الإلكترونية", "القطاع الحكومي", "الخدمات المصرفية والمالية",
  "الرعاية الصحية", "العقارات والمرافق", "التعليم", "الصناعة والتوزيع", "السيارات",
];
const PAIN_POINTS_AR = [
  "وقت طويل في إدخال البيانات يدويًا",
  "متابعة العملاء بطيئة أو غير منتظمة",
  "صعوبة العثور على المعلومات داخليًا",
  "تقارير تستغرق وقتًا طويلاً للتجهيز",
  "صعوبة في التنبؤ بالطلب أو المخزون",
];
const LIBRARY_AR = [
  { id: "copilot", title: "مساعد ذكاء اصطناعي للفرق الداخلية", effort: "متوسط", value: "مرتفعة", match: ["وقت طويل في إدخال البيانات يدويًا", "صعوبة العثور على المعلومات داخليًا"] },
  { id: "doc-ai", title: "معالجة المستندات (الفواتير، أوامر الشراء، النماذج)", effort: "منخفض", value: "مرتفعة", match: ["وقت طويل في إدخال البيانات يدويًا"] },
  { id: "anomaly", title: "كشف الشذوذ في المعاملات والمخزون", effort: "متوسط", value: "مرتفعة", match: ["صعوبة في التنبؤ بالطلب أو المخزون"] },
  { id: "lead-scoring", title: "تقييم وتوجيه العملاء المحتملين بالذكاء الاصطناعي", effort: "منخفض", value: "متوسطة", match: ["متابعة العملاء بطيئة أو غير منتظمة"] },
  { id: "chat-support", title: "تصنيف دعم العملاء بنموذج لغوي", effort: "منخفض", value: "مرتفعة", match: ["متابعة العملاء بطيئة أو غير منتظمة"] },
  { id: "reports", title: "مولّد تقارير بلغة طبيعية", effort: "متوسط", value: "متوسطة", match: ["تقارير تستغرق وقتًا طويلاً للتجهيز"] },
  { id: "forecast", title: "نموذج تنبؤ بالطلب والمخزون", effort: "مرتفع", value: "مرتفعة", match: ["صعوبة في التنبؤ بالطلب أو المخزون"] },
  { id: "search", title: "بحث داخلي دلالي في مستندات الشركة", effort: "متوسط", value: "مرتفعة", match: ["صعوبة العثور على المعلومات داخليًا"] },
];

function suggestAr(pains) {
  const scored = LIBRARY_AR.map((l) => ({ ...l, score: l.match.reduce((s, m) => s + (pains.includes(m) ? 2 : 0), 0) }));
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 3).map(({ id, title, effort, value }) => ({ id, title, effort, value }));
}

export function AiOpportunityAr() {
  const [step, setStep] = useState(0);
  const [industry, setIndustry] = useState("");
  const [pains, setPains] = useState([]);
  const [contact, setContact] = useState({ name: "", email: "", company: "" });
  const [state, setState] = useState({ stage: "quiz", error: "" });
  const total = 3;
  const togglePain = (p) => setPains((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));

  if (state.stage === "result") {
    const suggestions = state.result;
    return (
      <ArToolShell eyebrow="مكتشف فرص الذكاء الاصطناعي" title="حالات الاستخدام المقترحة لكم">
        <Seo title="فرص الذكاء الاصطناعي لأعمالكم" />
        <div className="space-y-4">
          {suggestions.map((s) => (
            <div key={s.id} data-testid={`ai-suggestion-ar-${s.id}`} className="rounded-lg border border-wafa-deep/10 bg-white p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-wafa-primary text-white">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div className="font-heading text-lg font-semibold text-wafa-deep">{s.title}</div>
                </div>
                <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider">
                  <span className="rounded-full bg-wafa-cream px-2 py-1 text-wafa-deep">الجهد · {s.effort}</span>
                  <span className="rounded-full bg-wafa-primary/10 px-2 py-1 text-wafa-deep">القيمة · {s.value}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <Link to="/ar/company/contact" data-testid="ai-result-cta-ar" className="mt-8 inline-flex w-full items-center justify-center rounded-md bg-wafa-primary px-5 py-3 text-sm font-semibold text-white hover:bg-wafa-deep">احجزوا سباق اكتشاف الذكاء الاصطناعي لأسبوعين</Link>
      </ArToolShell>
    );
  }

  if (state.stage === "gate") {
    const submit = async (e) => {
      e.preventDefault();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) { setState({ ...state, error: "يرجى إدخال بريد إلكتروني صحيح." }); return; }
      const suggestions = suggestAr(pains);
      try {
        await api.post("/tools/ai-opportunity", {
          industry, pain_points: pains, suggestions,
          email: contact.email, name: contact.name, company: contact.company,
        });
        setState({ stage: "result", result: suggestions });
      } catch (_) { setState({ ...state, error: "فشل الإرسال. يرجى المحاولة مجددًا." }); }
    };
    return (
      <ArToolShell eyebrow="مكتشف فرص الذكاء الاصطناعي" title="اطلعوا على أفكاركم" step={total} totalSteps={total}>
        <Seo title="مكتشف فرص الذكاء الاصطناعي" />
        <form onSubmit={submit} className="space-y-4" data-testid="ai-gate-form-ar">
          <input placeholder="الاسم" data-testid="ai-name-ar" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
          <input required type="email" placeholder="البريد الإلكتروني للعمل *" data-testid="ai-email-ar" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
          <input placeholder="الشركة" data-testid="ai-company-ar" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} />
          {state.error ? <div className="flex items-center gap-2 text-sm text-wafa-tomato"><AlertCircle className="h-4 w-4" />{state.error}</div> : null}
          <button data-testid="ai-see-result-ar" className="w-full rounded-md bg-wafa-primary py-3 text-sm font-semibold text-white hover:bg-wafa-deep">اطلعوا على الأفكار الثلاث</button>
        </form>
      </ArToolShell>
    );
  }

  return (
    <ArToolShell eyebrow="مكتشف فرص الذكاء الاصطناعي" title="أين يساعد الذكاء الاصطناعي فعليًا في أعمالكم." subtitle="خياران سريعان. سنعيد ثلاث أفكار مرتبة حسب الجهد مقابل القيمة." step={step + 1} totalSteps={total}>
      <Seo title="مكتشف فرص الذكاء الاصطناعي" />
      {step === 0 && (
        <div>
          <div className="font-heading text-xl font-medium text-wafa-deep">الصناعة</div>
          <div className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {INDUSTRIES_AR.map((i) => (
              <ArOptionRow key={i} value={i} current={industry} onChange={setIndustry} label={i} testId={`ai-industry-ar-${i}`} />
            ))}
          </div>
        </div>
      )}
      {step === 1 && (
        <div>
          <div className="font-heading text-xl font-medium text-wafa-deep">نقاط الألم. اختاروا ما ينطبق.</div>
          <div className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {PAIN_POINTS_AR.map((p) => {
              const active = pains.includes(p);
              return (
                <button key={p} type="button" onClick={() => togglePain(p)} data-testid={`ai-pain-ar-${p}`}
                  className={cn("rounded-md border px-4 py-3 text-start text-sm transition-colors",
                    active ? "border-wafa-primary bg-wafa-primary/5 text-wafa-deep font-semibold" : "border-wafa-deep/15 bg-white text-wafa-ink hover:border-wafa-deep/40")}>
                  {p}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <ArToolNav onBack={step > 0 ? () => setStep(step - 1) : null}
        onNext={() => { if (step === 1) setState({ stage: "gate", error: "" }); else setStep(step + 1); }}
        disableNext={step === 0 ? !industry : pains.length === 0}
        nextLabel={step === 1 ? "إنهاء" : "التالي"} />
    </ArToolShell>
  );
}
